import { useEffect, useRef } from "react";

// The season's scenery behind the greeting: one canvas, ~4 s of particles (snow / leaves / petals / rain / sun motes / sparks)
// plus frost dendrites growing in from the edges for the cold months. Pure canvas, no assets, pauses with prefers-reduced-motion.
const rnd = (a, b) => a + Math.random() * (b - a);

const spawn = (effect, W, H, s) => {
  const p = { x: rnd(-40, W + 40), y: rnd(-H * 0.2, H), r: 1, vx: 0, vy: 0, a: 1, rot: rnd(0, 6.28), vr: 0, color: "#fff" };
  if (effect === "snow") Object.assign(p, { r: rnd(1, 3.6), vx: rnd(-0.25, 0.25), vy: rnd(0.35, 1.3), a: rnd(0.35, 0.95), sway: rnd(0.4, 1.4), ph: rnd(0, 6.28) });
  else if (effect === "leaves") Object.assign(p, { r: rnd(5, 11), vx: rnd(-0.6, 0.2), vy: rnd(0.6, 1.6), a: rnd(0.55, 0.95), vr: rnd(-0.03, 0.03), sway: rnd(0.6, 1.6), ph: rnd(0, 6.28), color: s.leaf[Math.floor(rnd(0, s.leaf.length))] });
  else if (effect === "petals") Object.assign(p, { r: rnd(3, 6), vx: rnd(-0.5, 0.5), vy: rnd(0.35, 1), a: rnd(0.5, 0.9), vr: rnd(-0.04, 0.04), sway: rnd(0.5, 1.4), ph: rnd(0, 6.28), color: (s.petal || ["#fff", "#ffd6e8", "#ffb7d5"])[Math.floor(rnd(0, 3))] });
  else if (effect === "rain") Object.assign(p, { r: rnd(8, 18), vx: -0.6, vy: rnd(7, 11), a: rnd(0.12, 0.3) });
  else if (effect === "sun" || effect === "haze" || effect === "motes" || effect === "stars") Object.assign(p, { r: rnd(0.8, 2.6), vx: rnd(-0.15, 0.15), vy: rnd(-0.25, -0.05), a: rnd(0.15, 0.6), ph: rnd(0, 6.28), color: s.mote || (effect === "sun" ? "#ffe6a8" : "#ffd9a0") });
  else if (effect === "bokeh") Object.assign(p, { r: rnd(6, 24), vx: rnd(-0.12, 0.12), vy: rnd(-0.16, -0.03), a: rnd(0.06, 0.2), ph: rnd(0, 6.28), color: s.bokeh[Math.floor(rnd(0, s.bokeh.length))] });
  else if (effect === "sparks") Object.assign(p, { r: rnd(0.8, 2.2), vx: rnd(-0.3, 0.3), vy: rnd(-0.6, -0.15), a: rnd(0.3, 1), ph: rnd(0, 6.28), color: ["#ffd27a", "#ff9a5c", "#fff"][Math.floor(rnd(0, 3))] });
  else if (effect === "night") Object.assign(p, { r: rnd(0.6, 1.8), vx: rnd(-0.1, 0.1), vy: rnd(-0.2, -0.04), a: rnd(0.15, 0.55), ph: rnd(0, 6.28), color: "#f5d78a" });
  return p;
};

const COUNT = { snow: 140, leaves: 42, petals: 60, rain: 110, sun: 70, haze: 90, sparks: 70, night: 50, bokeh: 34, motes: 60, stars: 26, none: 0 };
const GLOWY = new Set(["sun", "sparks", "haze", "night", "motes", "stars", "bokeh"]);  // pulsing alpha + soft halo

// Night Sky: a still star field in Xpattern blue, drawn once (no moon, no lanterns)
const drawStars = (ctx, W, H) => {
  ctx.save();
  for (let i = 0; i < 220; i++) {
    const r = rnd(0.3, 1.5), x = rnd(0, W), y = rnd(0, H);
    ctx.fillStyle = i % 5 === 0 ? "#bfe0ff" : "#ffffff";
    ctx.globalAlpha = rnd(0.15, 0.9);
    ctx.beginPath(); ctx.arc(x, y, r, 0, 6.28); ctx.fill();
    if (r > 1.2) { ctx.globalAlpha *= 0.2; ctx.beginPath(); ctx.arc(x, y, r * 3, 0, 6.28); ctx.fill(); }
  }
  ctx.restore();
};

// Ramadan / Eid sky, drawn once: a star field and a gold crescent (moon disc, shadow disc clipped to it) high on the right
const drawNight = (ctx, W, H) => {
  ctx.save();
  ctx.fillStyle = "#fff7e0";
  for (let i = 0; i < 160; i++) { ctx.globalAlpha = rnd(0.2, 0.9); ctx.beginPath(); ctx.arc(rnd(0, W), rnd(0, H * 0.8), rnd(0.4, 1.4), 0, 6.28); ctx.fill(); }
  const mx = W * 0.72, my = H * 0.2, R = Math.max(26, Math.min(W, H) * 0.07);
  const glow = ctx.createRadialGradient(mx, my, R * 0.5, mx, my, R * 4.5);
  glow.addColorStop(0, "rgba(245,215,138,.32)"); glow.addColorStop(1, "rgba(245,215,138,0)");
  ctx.globalAlpha = 1; ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(mx, my, R * 4.5, 0, 6.28); ctx.fill();
  const moon = document.createElement("canvas");  // crescent cut on its own layer so the sky glow shows through the dark side
  moon.width = moon.height = Math.ceil(R * 2.4);
  const mc = moon.getContext("2d"), c = moon.width / 2;
  mc.fillStyle = "#f7e1a4"; mc.beginPath(); mc.arc(c, c, R, 0, 6.28); mc.fill();
  mc.globalCompositeOperation = "destination-out"; mc.beginPath(); mc.arc(c - R * 0.5, c - R * 0.22, R * 0.88, 0, 6.28); mc.fill();
  ctx.drawImage(moon, mx - c, my - c);
  ctx.restore();
};

// hanging lanterns left and right of the greeting (x, string length, scale) — each sways gently on its string
const LANTERNS = [[0.07, 0.30, 0.7], [0.14, 0.17, 1], [0.22, 0.36, 0.8], [0.82, 0.40, 0.75], [0.90, 0.24, 0.95], [0.96, 0.13, 0.7]];
const drawLantern = (ctx, x, y, s, t, ph, alpha) => {
  const w = 24 * s, h = 50 * s;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, 0); ctx.rotate(Math.sin(t / 70 + ph) * 0.045);
  ctx.strokeStyle = "rgba(245,215,138,.45)"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, y); ctx.stroke();
  ctx.translate(0, y);
  const glow = ctx.createRadialGradient(0, h * 0.5, 2, 0, h * 0.5, w * 3.4);
  glow.addColorStop(0, `rgba(255,190,90,${0.42 + 0.06 * Math.sin(t / 9 + ph)})`); glow.addColorStop(1, "rgba(255,190,90,0)");
  ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(0, h * 0.5, w * 3.4, 0, 6.28); ctx.fill();
  ctx.fillStyle = "#8a6a2a";
  ctx.beginPath(); ctx.moveTo(-w * 0.45, 0); ctx.lineTo(w * 0.45, 0); ctx.lineTo(w * 0.3, h * 0.12); ctx.lineTo(-w * 0.3, h * 0.12); ctx.closePath(); ctx.fill();
  const body = ctx.createLinearGradient(0, h * 0.12, 0, h * 0.9);
  body.addColorStop(0, "#ffd98a"); body.addColorStop(0.5, "#f2a63c"); body.addColorStop(1, "#c4701d");
  ctx.fillStyle = body;
  ctx.beginPath(); ctx.moveTo(-w * 0.3, h * 0.12); ctx.bezierCurveTo(-w * 0.95, h * 0.3, -w * 0.95, h * 0.7, -w * 0.3, h * 0.88);
  ctx.lineTo(w * 0.3, h * 0.88); ctx.bezierCurveTo(w * 0.95, h * 0.7, w * 0.95, h * 0.3, w * 0.3, h * 0.12); ctx.closePath(); ctx.fill();
  ctx.strokeStyle = "rgba(90,50,10,.4)"; ctx.lineWidth = 1;
  for (const k of [-0.55, 0, 0.55]) { ctx.beginPath(); ctx.moveTo(k * w * 0.5, h * 0.14); ctx.quadraticCurveTo(k * w * 1.15, h * 0.5, k * w * 0.5, h * 0.86); ctx.stroke(); }
  ctx.fillStyle = "#8a6a2a"; ctx.fillRect(-w * 0.32, h * 0.88, w * 0.64, h * 0.06);
  ctx.beginPath(); ctx.arc(0, h * 1.01, w * 0.1, 0, 6.28); ctx.fill();
  ctx.restore();
};

// frost: dendrites from the corners / top edge — a random walk with side branches, drawn once at low alpha
const drawFrost = (ctx, W, H, strength) => {
  ctx.save();
  ctx.lineCap = "round";
  const seeds = [[0, 0, 0.8], [W, 0, 2.4], [0, H, -0.8], [W, H, -2.4], [W * 0.5, 0, 1.57], [W * 0.25, 0, 1.3], [W * 0.75, 0, 1.9]];
  for (const [sx, sy, dir] of seeds) {
    for (let b = 0; b < 5; b++) {
      let x = sx, y = sy, ang = dir + rnd(-0.6, 0.6), len = rnd(90, 230) * strength;
      ctx.strokeStyle = `rgba(214,236,255,${0.22 * strength})`;
      ctx.lineWidth = rnd(0.8, 1.8);
      ctx.beginPath(); ctx.moveTo(x, y);
      for (let i = 0; i < 26 && len > 4; i++) {
        ang += rnd(-0.35, 0.35);
        const step = len / 7;
        x += Math.cos(ang) * step; y += Math.sin(ang) * step; len *= 0.88;
        ctx.lineTo(x, y);
        if (i % 2 === 0) {  // side crystal
          const side = ang + (i % 4 === 0 ? 1.05 : -1.05), l2 = step * rnd(0.6, 1.4);
          ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(side) * l2, y + Math.sin(side) * l2); ctx.moveTo(x, y);
        }
      }
      ctx.stroke();
    }
  }
  ctx.restore();
};

export const IntroScene = ({ season }) => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const ctx = cv.getContext("2d");
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * dpr; cv.height = H * dpr; ctx.scale(dpr, dpr);
    const frost = document.createElement("canvas");
    frost.width = W * dpr; frost.height = H * dpr;
    const night = season.effect === "night", stars = season.effect === "stars";
    if (season.frost || night || stars) { const fc = frost.getContext("2d"); fc.scale(dpr, dpr); if (night) drawNight(fc, W, H); else if (stars) drawStars(fc, W, H); else drawFrost(fc, W, H, season.frost); }
    const quick = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const ps = Array.from({ length: COUNT[season.effect] || 0 }, () => spawn(season.effect, W, H, season));
    let raf, t = 0, alive = true;
    const frame = () => {
      if (!alive) return;
      t += 1;
      ctx.clearRect(0, 0, W, H);
      if (season.frost || night || stars) { ctx.globalAlpha = Math.min(1, t / 90); ctx.drawImage(frost, 0, 0, W, H); ctx.globalAlpha = 1; }
      if (night) LANTERNS.forEach(([fx, fy, s], i) => drawLantern(ctx, fx * W, fy * H * (W < 700 ? 0.6 : 1), s * Math.max(0.8, Math.min(1.3, W / 1400)), t, i * 1.3, Math.min(1, Math.max(0, (t - 10 - i * 6) / 60))));
      for (const p of ps) {
        const sway = p.sway ? Math.sin(t / 40 + p.ph) * p.sway : 0;
        p.x += p.vx + sway * 0.3; p.y += p.vy; p.rot += p.vr;
        if (p.y > H + 30 || p.y < -40 || p.x < -60 || p.x > W + 60) Object.assign(p, spawn(season.effect, W, H, season), { y: p.vy > 0 ? -20 : H + 20 });
        ctx.globalAlpha = p.a * (p.ph !== undefined && GLOWY.has(season.effect) ? 0.55 + 0.45 * Math.sin(t / 25 + p.ph) : 1);
        ctx.fillStyle = p.color; ctx.strokeStyle = p.color;
        if (season.effect === "rain") { ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + p.vx * 2, p.y + p.r); ctx.stroke(); }
        else if (season.effect === "leaves" || season.effect === "petals") { ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot); ctx.beginPath(); ctx.ellipse(0, 0, p.r, p.r * 0.55, 0, 0, 6.28); ctx.fill(); ctx.restore(); }
        else if (season.effect === "bokeh") { const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r); g.addColorStop(0, p.color); g.addColorStop(0.7, p.color); g.addColorStop(1, "rgba(255,220,150,0)"); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.28); ctx.fill(); }
        else { ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.28); ctx.fill(); if (GLOWY.has(season.effect)) { ctx.globalAlpha *= 0.25; ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 3, 0, 6.28); ctx.fill(); } }
      }
      ctx.globalAlpha = 1;
      if (!quick) raf = requestAnimationFrame(frame);
    };
    frame();
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [season]);
  return <canvas ref={ref} className="intro-scene" aria-hidden data-testid="intro-scene" data-effect={season.effect} />;
};
